/**
 * DailyReviewBanner — Mastery W3 academy banner.
 *
 * Compute today's review queue using W2 buildReviewQueue + handleLongAbsence,
 * surface a single CTA banner at the top of the academy/home nav layer.
 *
 * 屬「academy nav 層」 retention banner — boss memory feedback_learn_pro_mode
 * explicitly allows banner on home/academy; quiz screen is OUT of scope.
 *
 * Banner states:
 *   N === 0           → 「今日無待複習,完美!」 (professional, no戲謔)
 *   N >  0 (normal)   → 「今日待複習 X 題(預計 X×30sec = Y 分鐘)」 + CTA
 *   long absence      → 黃底「歡迎回來!部分題目已自動降級複習」 + CTA
 *
 * Dispatches `mastery:review:start` CustomEvent on `window` carrying the queue.
 * (W4 will hook this into a real quiz screen; this PR only emits the event.)
 *
 * Schema (src/shared/types.ts L415-427) — uses real StudentKP only.
 */

import type { StudentKP } from '../../shared/types';
import { buildReviewQueue, handleLongAbsence } from '../mastery';

/** Estimate per-question answer time used to translate queue size → minutes. */
export const SECONDS_PER_QUESTION = 30;

/** Long-absence threshold mirrors W2 handleLongAbsence — 7 days. */
const MS_PER_DAY = 24 * 60 * 60 * 1000;
const ABSENCE_THRESHOLD_MS = 7 * MS_PER_DAY;

/** CustomEvent name dispatched on `window` when the CTA is clicked. */
export const REVIEW_START_EVENT = 'mastery:review:start';

/**
 * Whether the student has been absent strictly more than 7 days since their
 * most-recent review. Mirrors the rule inside handleLongAbsence for UI use.
 */
function isLongAbsence(kps: ReadonlyArray<StudentKP>, now: number): boolean {
  let mostRecent = Number.NEGATIVE_INFINITY;
  let ever = false;
  for (const kp of kps) {
    if (!kp.lastReviewedISO) continue;
    const t = Date.parse(kp.lastReviewedISO);
    if (Number.isFinite(t)) {
      ever = true;
      if (t > mostRecent) mostRecent = t;
    }
  }
  if (!ever) return false;
  return now - mostRecent > ABSENCE_THRESHOLD_MS;
}

/**
 * Convert queue size N → minutes-display string.
 *   N * 30 sec rounded UP to the nearest minute (kids prefer
 *   conservative time budgets — 4 題 = 2 分鐘, not 1.5).
 */
export function minutesForQueue(queueSize: number): number {
  if (queueSize <= 0) return 0;
  const seconds = queueSize * SECONDS_PER_QUESTION;
  return Math.max(1, Math.ceil(seconds / 60));
}

/**
 * Render the daily review banner into `container`.
 *
 * @param container target DOM. Null/undefined → no-op.
 * @param kps       student's full KP list (undefined → empty).
 * @param now       epoch ms; pass Date.now() at the call site. When omitted
 *                  here we fall back to Date.now() (for tests passing only
 *                  the required args).
 * @param onStart   optional callback invoked when the CTA is clicked.
 *                  We ALWAYS also dispatch `mastery:review:start` on window
 *                  with `detail.queue`, regardless of whether onStart is set.
 */
export function renderDailyReviewBanner(
  container: HTMLElement,
  kps: StudentKP[] | undefined,
  now: number = Date.now(),
  onStart?: () => void,
): void {
  if (!container) return;

  // Idempotent re-render
  const prior = container.querySelector('[data-testid="daily-review-banner"]');
  if (prior) prior.remove();

  const list = kps ?? [];
  const longAbsence = isLongAbsence(list, now);

  // Apply long-absence demote BEFORE computing today's queue so users see
  // the same items the algorithm will surface in W4.
  const effective = longAbsence ? handleLongAbsence(list, now) : list;
  const queue = buildReviewQueue(effective, now, { includeDueOnly: true });
  const n = queue.length;
  const minutes = minutesForQueue(n);

  const banner = document.createElement('div');
  banner.className = 'daily-review-banner';
  banner.setAttribute('data-testid', 'daily-review-banner');
  banner.setAttribute('data-review-count', String(n));
  if (longAbsence) banner.setAttribute('data-long-absence', 'true');

  const baseCss = [
    'display:flex',
    'align-items:center',
    'justify-content:space-between',
    'gap:12px',
    'padding:12px 16px',
    'border-radius:10px',
    'margin:8px 0',
    'font-size:14px',
    'line-height:1.4',
  ].join(';');

  let variantCss: string;
  let title: string;
  let showCTA: boolean;
  let ctaLabel = '開始複習 →';

  if (longAbsence) {
    // Yellow (warm) welcome-back state.
    variantCss = ';background:#FFF59D;color:#5D4037;border:1px solid #FBC02D;';
    title = '歡迎回來!部分題目已自動降級複習';
    showCTA = true;
  } else if (n === 0) {
    // Calm green confirmation. Boss memory feedback_learn_pro_mode:
    // 「教學是要專業的」 — no exclamation drama beyond one period.
    variantCss = ';background:#E0F2F1;color:#004D40;border:1px solid #80CBC4;';
    title = '今日無待複習,完美!';
    showCTA = false;
  } else {
    // Default — neutral cool blue.
    variantCss = ';background:#E1F5FE;color:#01579B;border:1px solid #4FC3F7;';
    title = `今日待複習 ${n} 題(預計 ${n}×30sec = ${minutes} 分鐘)`;
    showCTA = true;
  }

  banner.style.cssText = baseCss + variantCss;

  const left = document.createElement('div');
  left.setAttribute('data-testid', 'daily-review-banner-title');
  left.style.cssText = 'flex:1 1 auto;font-weight:600;';
  left.textContent = title;
  banner.appendChild(left);

  if (showCTA) {
    const cta = document.createElement('button');
    cta.type = 'button';
    cta.className = 'primary-btn daily-review-cta';
    cta.setAttribute('data-testid', 'daily-review-cta');
    cta.textContent = ctaLabel;
    cta.style.cssText = [
      'padding:8px 14px',
      'border-radius:6px',
      'border:none',
      'background:#0277BD',
      'color:#fff',
      'font-weight:700',
      'cursor:pointer',
      'font-size:13px',
    ].join(';');
    cta.addEventListener('click', () => {
      window.dispatchEvent(
        new CustomEvent(REVIEW_START_EVENT, { detail: { queue } }),
      );
      onStart?.();
    });
    banner.appendChild(cta);
  }

  container.appendChild(banner);
}
